import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  FolderOpen,
  Mic2,
  BookOpen,
  Clock,
  TrendingUp,
  ArrowRight,
  AlertCircle,
  Zap,
  Users,
  Building2,
  Rocket,
} from "lucide-react";

import adminService from "@/services/adminService";

// Safe render helper to prevent "Objects are not valid as React child" errors
const safeRender = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "string" || typeof val === "number") return val;
  if (Array.isArray(val)) return val.map(safeRender).join(", ");
  if (typeof val === "object") {
    // Try common label keys
    return val.label || val.name || val.key || val.title || val.message || JSON.stringify(val);
  }
  return String(val);
};

const SEVERITY_COLORS = {
  INFO: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  WARN: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  CRITICAL: "bg-red-500/20 text-red-300 border-red-500/30",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [events, setEvents] = useState([]);
  const [queues, setQueues] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [metricsData, eventsData, queuesData] = await Promise.all([
        adminService.getMetrics(),
        adminService.getEvents({ is_read: false }),
        adminService.getQueues(),
      ]);
      setMetrics(metricsData);
      setEvents(Array.isArray(eventsData) ? eventsData.slice(0, 8) : []);
      setQueues(queuesData);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (eventId) => {
    try {
      await adminService.markEventRead(eventId);
      setEvents(events.filter((e) => e.id !== eventId));
      toast.success("Event marked as read");
    } catch (error) {
      console.error("Error marking event:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading Mission Control...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Mission Control</h1>
          <p className="text-slate-400">
            Real-time overview of your audiobook production pipeline
          </p>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="border-slate-400 text-white bg-slate-700/50 hover:bg-slate-600"

        >
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
          onClick={() => navigate("/admin/queues")}
          data-testid="kpi-approvals"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Pending Approvals</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {metrics?.approvals_pending_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
          onClick={() => navigate("/admin/queues")}
          data-testid="kpi-funding"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Funding Ready</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {metrics?.funding_threshold_reached_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
          onClick={() => navigate("/admin/queues")}
          data-testid="kpi-qc"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Open QC Issues</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {metrics?.qc_open_issues_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
          onClick={() => navigate("/admin/books")}
          data-testid="kpi-recordings"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Recordings</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {metrics?.active_recordings_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Mic2 className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <BookOpen className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-2xl font-bold text-white">{metrics?.total_books || 0}</p>
              <p className="text-xs text-slate-400">Total Books</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <Users className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-2xl font-bold text-white">{metrics?.total_narrators || 0}</p>
              <p className="text-xs text-slate-400">Narrators</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <Building2 className="w-8 h-8 text-orange-400" />
            <div>
              <p className="text-2xl font-bold text-white">{metrics?.total_publishers || 0}</p>
              <p className="text-xs text-slate-400">Publishers</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <Rocket className="w-8 h-8 text-pink-400" />
            <div>
              <p className="text-2xl font-bold text-white">{metrics?.launched_count || 0}</p>
              <p className="text-xs text-slate-400">Launched</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Critical Alerts */}
        <div className="lg:col-span-2">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Critical Alerts
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin/queues")}
                  className="text-white bg-slate-700/50 hover:bg-slate-600 border border-slate-500"

                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {events.length > 0 ? (
                events.map((event) => (
                  <div
                    key={event.id}
                    className={`p-3 rounded-lg border ${
                      SEVERITY_COLORS[event.severity] || SEVERITY_COLORS.INFO
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant="outline"
                            className="text-xs border-current"
                          >
                            {event.event_type}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {safeRender(event.entity_type)}
                          </Badge>
                        </div>
                        <p className="text-sm">{safeRender(event.message)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMarkRead(event.id)}
                        className="text-slate-400 hover:text-white"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No critical alerts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Today's Money Flow */}
        <div>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Today's Payments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {metrics?.payments_today_total &&
              Object.keys(metrics.payments_today_total).length > 0 ? (
                Object.entries(metrics.payments_today_total).map(([currency, amount]) => (
                  <div
                    key={currency}
                    className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg"
                  >
                    <span className="text-slate-300 font-medium">{currency}</span>
                    <span className="text-white font-bold">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: currency,
                      }).format(amount)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No payments today</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-slate-800/50 border-slate-700 mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={() => navigate("/admin/queues")}
                variant="outline"
                className="w-full justify-start border-slate-500 text-white bg-slate-800 hover:bg-slate-700 hover:border-purple-500"

              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Review Approvals
                {queues?.approvals?.length > 0 && (
                  <Badge className="ml-auto bg-yellow-500/20 text-yellow-300">
                    {queues.approvals.length}
                  </Badge>
                )}
              </Button>
              <Button
                onClick={() => navigate("/admin/queues")}
                variant="outline"
                className="w-full justify-start border-slate-500 text-white bg-slate-800 hover:bg-slate-700 hover:border-purple-500"

              >
                <DollarSign className="w-4 h-4 mr-2" />
                Activate Funding
                {queues?.funding_activation?.length > 0 && (
                  <Badge className="ml-auto bg-green-500/20 text-green-300">
                    {queues.funding_activation.length}
                  </Badge>
                )}
              </Button>
              <Button
                onClick={() => navigate("/admin/workspaces")}
                variant="outline"
                className="w-full justify-start border-slate-500 text-white bg-slate-800 hover:bg-slate-700 hover:border-purple-500"

              >
                <FolderOpen className="w-4 h-4 mr-2" />
                View User Workspaces
              </Button>
              <Button
                onClick={() => navigate("/admin/narrators")}
                variant="outline"
                className="w-full justify-start border-slate-500 text-white bg-slate-800 hover:bg-slate-700 hover:border-purple-500"

              >
                <Mic2 className="w-4 h-4 mr-2" />
                Assign Narrators
              </Button>
              <Button
                onClick={() => navigate("/admin/queues")}
                variant="outline"
                className="w-full justify-start border-slate-500 text-white bg-slate-800 hover:bg-slate-700 hover:border-purple-500"

              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Review QC
                {queues?.qc_issues?.length > 0 && (
                  <Badge className="ml-auto bg-red-500/20 text-red-300">
                    {queues.qc_issues.length}
                  </Badge>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
